import React, { useState, useEffect, useRef } from 'react';
import {
  UserGroupIcon,
  ChartBarIcon,
  CalendarIcon,
  CheckCircleIcon,
  ClockIcon,
  UsersIcon,
  SparklesIcon,
  TicketIcon,
  MegaphoneIcon,
  TrophyIcon,
  CurrencyEuroIcon,
  ExclamationCircleIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  PhotoIcon,
  ArrowTrendingUpIcon,
  ArrowUpRightIcon,
  ArrowDownRightIcon,
  MinusIcon,
  UserPlusIcon,
  UserMinusIcon,
  TagIcon,
  CalendarDaysIcon,
  AcademicCapIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../utils/apiClient';

import LineChart from '../components/LineChart';
import AttributeLabel from '../components/AttributeLabel';
import { StatCard, StatGroup } from '../components/StatCard';

interface MemberGrowthItem {
  month: string;
  new_members: number;
  deleted_members: number;
  net_change: number;
  cumulative_total: number;
  active_members: number;
}

interface MemberGrowthData {
  all: MemberGrowthItem[];
  guests: MemberGrowthItem[];
  members: MemberGrowthItem[];
}

interface MemberActivity {
  month: string;
  active_members: number;
  total_bookings: number;
  unique_events: number;
  avg_bookings_per_member: number;
}

interface EventPopularity {
  unique_id: string;
  title: string;
  event_count: number;
  total_bookings: number;
  max_teilnehmer: number;
  avg_utilization: number;
  avg_bookings_per_event: number;
  trainer_name?: string;
  cotrainer_name?: string;
}

interface CurrentStats {
  undated_exits?: number;
  planned_exits_year?: number;
  total_members: number;
  new_members_year: number;
  left_members_year: number;
  active_members_year: number;
  bookings_year: number;
  total_events_year: number;
  unique_event_types_year: number;
  avg_bookings_per_member_year: number;
}

interface ProfileCompleteness {
  overall?: {
    total_members: number;
    avg_completeness: number;
    with_photo: number;
    with_email: number;
    with_phone: number;
    with_birthdate: number;
    with_address: number;
    fully_complete: number;
    missing_critical: number;
  };
  distribution?: Array<{
    category: string;
    count: number;
    percentage: number;
    avg_completeness: number;
  }>;
  top_incomplete?: Array<{
    name: string;
    completeness: number;
    missing_fields: number;
    has_photo: number;
    has_email: number;
    has_phone: number;
  }>;
}

interface TopTrainer {
  trainer_name: string;
  total_events: number;
  unique_event_types: number;
  total_bookings: number;
  unique_participants: number;
  avg_utilization: number;
  avg_bookings_per_event: number;
  success_rate: number;
  event_types: string;
}

interface TopMember {
  member_name: string;
  total_bookings: number;
  unique_event_types: number;
  active_days: number;
  active_months: number;
  first_booking: string;
  last_booking: string;
  event_types: string;
  participation_rate: number;
  avg_bookings_per_month: number;
}

interface GuestCardStats {
  revenue: { total: number; paid: number; unpaid: number };
  cards: { total: number; active: number; depleted: number; soldThisMonth: number; soldLastMonth: number };
  bookings: { total: number; thisMonth: number; lastMonth: number; uniqueEvents: number };
  averages: { entriesPerCard: number; pricePerCard: number };
  typeDistribution: Array<{ name: string; count: number; revenue: number; avgRemaining: number }>;
  monthlyRevenue: Array<{ month: string; revenue: number; cardsSold: number; uniqueBuyers: number }>;
}

interface TabDataCache {
  memberGrowth?: MemberGrowthData;
  memberActivity?: MemberActivity[];
  eventPopularity?: EventPopularity[];
  activityTrends?: any;
  topEvents?: any[];
  profileCompleteness?: ProfileCompleteness;
  topTrainers?: TopTrainer[];
  topMembers?: TopMember[];
  guestCardStats?: GuestCardStats;
}

// Vorjahres-Daten (nur die jahresbezogenen Tabs, die keinen eigenen Overview-Bezug haben)
interface PrevTabDataCache {
  topTrainers?: TopTrainer[];
  topMembers?: TopMember[];
  guestCardStats?: GuestCardStats;
}

interface TabLoadingStates {
  growth: boolean;
  activity: boolean;
  events: boolean;
  profiles: boolean;
  trainers: boolean;
  members: boolean;
  guestcards: boolean;
}

type IconType = React.ComponentType<React.SVGProps<SVGSVGElement>>;
type Accent = 'gray' | 'success' | 'primary' | 'info' | 'warning' | 'error' | 'amber';

// Statische Accent-Klassen (kein dynamisches Tailwind → purge-sicher)
const ACCENTS: Record<Accent, { icon: string; iconBg: string; value: string; bar: string }> = {
  gray: { icon: 'text-gray-500 dark:text-gray-400', iconBg: 'bg-gray-100 dark:bg-gray-700/50', value: 'text-gray-900 dark:text-white', bar: 'bg-gray-400' },
  success: { icon: 'text-success-600 dark:text-success-400', iconBg: 'bg-success-50 dark:bg-success-900/20', value: 'text-success-600 dark:text-success-400', bar: 'bg-success-500' },
  primary: { icon: 'text-primary-600 dark:text-primary-400', iconBg: 'bg-primary-50 dark:bg-primary-900/20', value: 'text-primary-600 dark:text-primary-400', bar: 'bg-primary-500' },
  info: { icon: 'text-info-600 dark:text-info-400', iconBg: 'bg-info-50 dark:bg-info-900/20', value: 'text-info-600 dark:text-info-400', bar: 'bg-info-500' },
  warning: { icon: 'text-warning-600 dark:text-warning-400', iconBg: 'bg-warning-50 dark:bg-warning-900/20', value: 'text-warning-600 dark:text-warning-400', bar: 'bg-warning-500' },
  error: { icon: 'text-error-600 dark:text-error-400', iconBg: 'bg-error-50 dark:bg-error-900/20', value: 'text-error-600 dark:text-error-400', bar: 'bg-error-500' },
  amber: { icon: 'text-amber-600 dark:text-amber-400', iconBg: 'bg-amber-50 dark:bg-amber-900/20', value: 'text-amber-600 dark:text-amber-400', bar: 'bg-amber-500' },
};

const fmtEUR = (n: number) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n || 0);

const round1 = (n: number) => Math.round((n || 0) * 10) / 10;

// ---- Dezenter Vorjahresvergleich ----
const YoYBadge: React.FC<{
  current: number;
  previous?: number | null;
  prevYear: number;
  invert?: boolean; // true: Rückgang ist "gut"
  money?: boolean;
  unit?: string;
}> = ({ current, previous, invert = false, money = false, unit = '' }) => {
  if (previous === null || previous === undefined) return null;
  const diff = (current || 0) - (previous || 0);
  const neutral = Math.abs(diff) < 0.05;
  const good = invert ? diff < 0 : diff > 0;
  const color = neutral
    ? 'text-gray-400 dark:text-gray-500'
    : good
      ? 'text-success-600 dark:text-success-400'
      : 'text-error-500 dark:text-error-400';
  const Arrow = neutral ? MinusIcon : diff > 0 ? ArrowUpRightIcon : ArrowDownRightIcon;
  const val = money ? fmtEUR(Math.abs(diff)) : `${round1(Math.abs(diff))}${unit}`;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] leading-none whitespace-nowrap font-semibold ${color}`}>
      <Arrow className="h-3 w-3" />
      {neutral ? '±0' : `${diff > 0 ? '+' : '−'}${val}`}
    </span>
  );
};

// ---- Abschnitts-Karte ----
const Section: React.FC<{
  title: string;
  icon: IconType;
  iconAccent?: Accent;
  right?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, icon: Icon, iconAccent = 'primary', right, children }) => (
  <div className="bg-white dark:bg-[#1c2432] rounded-2xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
    <div className="flex items-center justify-between gap-3 mb-4">
      <div className="flex items-center gap-2 min-w-0">
        <Icon className={`h-5 w-5 flex-shrink-0 ${ACCENTS[iconAccent].icon}`} />
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white truncate">{title}</h3>
      </div>
      {right}
    </div>
    {children}
  </div>
);

// ---- Kurzbeschreibung ----
const InfoBanner: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-4 py-3">
    <p className="text-sm text-gray-600 dark:text-gray-300">{children}</p>
  </div>
);

// ---- Vorjahresvergleich-Strip (je Tab) ----
// Kennzahlenkarte nach Issue #10/#9: kleines semantisches Icon (h-4 w-4,
// neutral grau) direkt vor dem Label, Kennzahl darunter (tabular-nums).
const CompareStrip: React.FC<{
  prevYear: number;
  items: Array<{ label: string; icon?: IconType; current: number; previous?: number | null; money?: boolean; unit?: string; invert?: boolean }>;
}> = ({ prevYear, items }) => {
  const isThreeItemStrip = items.length === 3;

  return (
    <div role="group" aria-label={`Vergleich zu ${prevYear}`} className={isThreeItemStrip
      ? 'grid grid-cols-3 overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-[#1c2432] sm:gap-3 sm:overflow-visible sm:rounded-none sm:border-0 sm:bg-transparent dark:sm:bg-transparent'
      : 'grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3'
    }>
      {items.map((it, i) => {
        const Icon = it.icon;
        return (
          <div
            key={i}
            className={isThreeItemStrip
              ? 'min-w-0 border-r border-gray-200 p-2.5 last:border-r-0 dark:border-gray-700 sm:rounded-xl sm:border sm:bg-white sm:p-3 dark:sm:border-gray-700 dark:sm:bg-[#1c2432]'
              : 'min-w-0 rounded-xl border border-gray-200 bg-white p-2.5 dark:border-gray-700 dark:bg-[#1c2432] sm:p-3'
            }
          >
            <div className="flex min-h-7 items-start gap-1.5 sm:min-h-0">
              {Icon && <Icon className="mt-px h-4 w-4 flex-shrink-0 text-gray-500 dark:text-gray-400" aria-hidden="true" />}
              <p className="min-w-0 text-[9px] font-semibold uppercase leading-tight tracking-wide text-gray-500 dark:text-gray-400 sm:text-[10px] sm:tracking-wider">
                {it.label}
              </p>
            </div>
            <div className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 sm:block">
              <p className="text-lg font-bold leading-none text-gray-900 dark:text-white tabular-nums sm:text-xl sm:leading-normal">
                {it.money ? fmtEUR(it.current) : `${round1(it.current)}${it.unit || ''}`}
              </p>
              <div className="sm:mt-1">
                <YoYBadge current={it.current} previous={it.previous} prevYear={prevYear} money={it.money} unit={it.unit} invert={it.invert} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ---- Rang-Medaille ----
const RankMedal: React.FC<{ rank: number }> = ({ rank }) => {
  const styles =
    rank === 1 ? 'bg-warning-500 text-white' :
    rank === 2 ? 'bg-gray-400 text-white' :
    rank === 3 ? 'bg-amber-600 text-white' :
    'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300';
  return (
    <div className={`flex items-center justify-center w-10 h-10 rounded-full font-bold flex-shrink-0 ${styles}`}>
      {rank}
    </div>
  );
};

// ---- Kleine Kennzahl (Trainer/Mitglieder-Detail) ----
const MiniStat: React.FC<{ value: React.ReactNode; label: string; accent?: Accent }> = ({ value, label, accent = 'gray' }) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-2 text-center">
    <p className={`text-lg font-semibold tabular-nums ${ACCENTS[accent].value}`}>{value}</p>
    <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
  </div>
);

const EmptyState: React.FC<{ icon: IconType; text: string }> = ({ icon: Icon, text }) => (
  <div className="bg-white dark:bg-[#1c2432] rounded-2xl p-8 text-center border border-gray-200 dark:border-gray-700">
    <Icon className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
    <p className="text-gray-500 dark:text-gray-400">{text}</p>
  </div>
);

const TABS: Array<{ key: TabLoadingStates extends Record<infer K, any> ? K : never; label: string }> = [
  { key: 'growth', label: 'Mitgliederentwicklung' },
  { key: 'activity', label: 'Aktivität' },
  { key: 'events', label: 'Events' },
  { key: 'profiles', label: 'Profilvollständigkeit' },
  { key: 'trainers', label: 'Trainer' },
  { key: 'members', label: 'Top Mitglieder' },
  { key: 'guestcards', label: 'Gästekarten' },
];

const MemberStatistics: React.FC = () => {
  const [currentStats, setCurrentStats] = useState<CurrentStats | null>(null);
  const [prevStats, setPrevStats] = useState<CurrentStats | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [activeTab, setActiveTab] = useState<'growth' | 'activity' | 'events' | 'profiles' | 'trainers' | 'members' | 'guestcards'>('growth');
  // Mitgliederentwicklung immer für alle Mitglieder (Umschaltung entfernt).
  const memberType = 'all' as const;

  const [tabDataCache, setTabDataCache] = useState<TabDataCache>({});
  const [prevTabDataCache, setPrevTabDataCache] = useState<PrevTabDataCache>({});
  const [tabLoadingStates, setTabLoadingStates] = useState<TabLoadingStates>({
    growth: false, activity: false, events: false, profiles: false, trainers: false, members: false, guestcards: false,
  });

  const prevYear = selectedYear - 1;

  const selectedYearRef = useRef(selectedYear);
  selectedYearRef.current = selectedYear;
  const cacheYear = useRef<number | null>(null);

  useEffect(() => {
    if (cacheYear.current !== selectedYear) {
      cacheYear.current = selectedYear;
      setTabDataCache(prev => ({ memberGrowth: prev.memberGrowth }));
      setPrevTabDataCache({});
      setTabLoadingStates({
        growth: false, activity: false, events: false, profiles: false, trainers: false, members: false, guestcards: false,
      });
      setError(null);
      fetchOverviewStats();
      fetchTabData(activeTab);
    } else if (!checkTabDataExists(activeTab) && !tabLoadingStates[activeTab]) {
      fetchTabData(activeTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedYear]);

  const checkTabDataExists = (tab: string): boolean => {
    switch (tab) {
      case 'growth': return !!tabDataCache.memberGrowth;
      case 'activity': return !!tabDataCache.memberActivity && !!tabDataCache.activityTrends;
      case 'events': return !!tabDataCache.eventPopularity && !!tabDataCache.topEvents;
      case 'profiles': return !!tabDataCache.profileCompleteness;
      case 'trainers': return !!tabDataCache.topTrainers;
      case 'members': return !!tabDataCache.topMembers;
      case 'guestcards': return !!tabDataCache.guestCardStats;
      default: return false;
    }
  };

  const fetchOverviewStats = async () => {
    const requestedYear = selectedYear;
    try {
      setInitialLoading(true);
      const [curRes, prevRes] = await Promise.all([
        apiClient.get(`/api/member-statistics/stats/overview?year=${selectedYear}`),
        apiClient.get(`/api/member-statistics/stats/overview?year=${selectedYear - 1}`),
      ]);
      if (requestedYear !== selectedYearRef.current) return;
      if (curRes.data.success) setCurrentStats(curRes.data.data.currentStats);
      setPrevStats(prevRes.data.success ? prevRes.data.data.currentStats : null);
    } catch (err) {
      console.error('Error fetching overview statistics:', err);
      if (requestedYear === selectedYearRef.current) setError('Fehler beim Laden der Übersichtsstatistiken');
    } finally {
      if (requestedYear === selectedYearRef.current) setInitialLoading(false);
    }
  };

  const fetchTabData = async (tab: string) => {
    const requestedYear = selectedYear;
    setTabLoadingStates(prev => ({ ...prev, [tab]: true }));
    try {
      switch (tab) {
        case 'growth': {
          const res = await apiClient.get('/api/member-statistics/stats/member-growth-all');
          if (requestedYear !== selectedYearRef.current) return;
          if (res.data.success) setTabDataCache(prev => ({ ...prev, memberGrowth: res.data.data.memberGrowth }));
          break;
        }
        case 'activity': {
          const [activityRes, trendsRes] = await Promise.all([
            apiClient.get(`/api/member-statistics/stats/member-activity?year=${selectedYear}`),
            apiClient.get(`/api/member-statistics/stats/activity-trends?year=${selectedYear}`),
          ]);
          if (requestedYear !== selectedYearRef.current) return;
          if (activityRes.data.success && trendsRes.data.success) {
            setTabDataCache(prev => ({
              ...prev,
              memberActivity: activityRes.data.data.memberActivity,
              activityTrends: trendsRes.data.data.activityTrends,
            }));
          }
          break;
        }
        case 'events': {
          const res = await apiClient.get(`/api/member-statistics/stats/event-popularity?year=${selectedYear}`);
          if (requestedYear !== selectedYearRef.current) return;
          if (res.data.success) setTabDataCache(prev => ({ ...prev, eventPopularity: res.data.data.eventPopularity, topEvents: res.data.data.topEvents }));
          break;
        }
        case 'profiles': {
          const res = await apiClient.get('/api/member-statistics/stats/profiles');
          if (requestedYear !== selectedYearRef.current) return;
          if (res.data.success) setTabDataCache(prev => ({ ...prev, profileCompleteness: res.data.data.profileCompleteness }));
          break;
        }
        case 'trainers': {
          const [curRes, prevRes] = await Promise.all([
            apiClient.get(`/api/member-statistics/stats/trainers?year=${selectedYear}`),
            apiClient.get(`/api/member-statistics/stats/trainers?year=${selectedYear - 1}`),
          ]);
          if (requestedYear !== selectedYearRef.current) return;
          if (curRes.data.success) setTabDataCache(prev => ({ ...prev, topTrainers: curRes.data.data.topTrainers }));
          if (prevRes.data.success) setPrevTabDataCache(prev => ({ ...prev, topTrainers: prevRes.data.data.topTrainers }));
          break;
        }
        case 'members': {
          const [curRes, prevRes] = await Promise.all([
            apiClient.get(`/api/member-statistics/stats/top-members?year=${selectedYear}`),
            apiClient.get(`/api/member-statistics/stats/top-members?year=${selectedYear - 1}`),
          ]);
          if (requestedYear !== selectedYearRef.current) return;
          if (curRes.data.success) setTabDataCache(prev => ({ ...prev, topMembers: curRes.data.data.topMembers }));
          if (prevRes.data.success) setPrevTabDataCache(prev => ({ ...prev, topMembers: prevRes.data.data.topMembers }));
          break;
        }
        case 'guestcards': {
          const [curRes, prevRes] = await Promise.all([
            apiClient.get(`/api/member-statistics/stats/guest-cards?year=${selectedYear}`),
            apiClient.get(`/api/member-statistics/stats/guest-cards?year=${selectedYear - 1}`),
          ]);
          if (requestedYear !== selectedYearRef.current) return;
          if (curRes.data.success) setTabDataCache(prev => ({ ...prev, guestCardStats: curRes.data.data.guestCardStats }));
          if (prevRes.data.success) setPrevTabDataCache(prev => ({ ...prev, guestCardStats: prevRes.data.data.guestCardStats }));
          break;
        }
        default:
          break;
      }
    } catch (err) {
      console.error(`Error fetching ${tab} data:`, err);
      if (requestedYear === selectedYearRef.current) setError(`Fehler beim Laden der ${tab} Daten`);
    } finally {
      if (requestedYear === selectedYearRef.current) setTabLoadingStates(prev => ({ ...prev, [tab]: false }));
    }
  };

  // ---- LineChart-Daten (unverändert) ----
  const prepareMemberGrowthData = (type: 'all' | 'guests' | 'members' = memberType) => {
    if (!tabDataCache.memberGrowth || !tabDataCache.memberGrowth[type]) return [];
    return tabDataCache.memberGrowth[type].map((item: MemberGrowthItem) => ({
      date: item.month + '-01',
      purchases: { count: Number(item.new_members), revenue: 0, unique_buyers: 0 },
      payments: { count: Number(item.deleted_members), revenue: 0, avg_delay: null },
      bookings: { count: Number(item.active_members), unique_users: 0, unique_events: 0 },
      cumulative: { purchases: Number(item.cumulative_total), revenue: 0, payments: 0, bookings: Number(item.active_members) },
    }));
  };

  const prepareActivityData = () => {
    if (!tabDataCache.memberActivity) return [];
    return tabDataCache.memberActivity.map(item => ({
      date: item.month + '-01',
      purchases: { count: item.active_members, revenue: 0, unique_buyers: 0 },
      payments: { count: 0, revenue: 0, avg_delay: null },
      bookings: { count: item.total_bookings, unique_users: item.active_members, unique_events: item.unique_events },
      cumulative: { purchases: 0, revenue: 0, payments: 0, bookings: item.total_bookings },
    }));
  };

  // Jahres-Summen aus den All-Jahre-Wachstumsdaten (für Growth-Tab-Vergleich, ohne Extra-Request)
  const growthYearSummary = (type: 'all' | 'guests' | 'members', year: number) => {
    const rows = tabDataCache.memberGrowth?.[type]?.filter(r => r.month.startsWith(String(year))) || [];
    const sum = (k: keyof MemberGrowthItem) => rows.reduce((s, r) => s + Number(r[k] || 0), 0);
    return { neu: sum('new_members'), aus: sum('deleted_members'), netto: sum('net_change') };
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error && !currentStats) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-xl p-6">
          <p className="text-error-800 dark:text-error-200">{error}</p>
        </div>
      </div>
    );
  }

  const yearSelect = (
    <select
      aria-label="Statistikjahr"
      value={selectedYear}
      onChange={(e) => setSelectedYear(parseInt(e.target.value))}
      className="h-10 md:h-11 px-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-medium"
    >
      {Array.from({ length: new Date().getFullYear() - Math.min(new Date().getFullYear() - 4,
        ...(tabDataCache.memberGrowth?.all || []).map(row => Number(row.month.slice(0, 4)))) + 1 },
        (_, index) => new Date().getFullYear() - index).map(year => (
        <option key={year} value={year}>{year}</option>
      ))}
    </select>
  );

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header (Desktop) */}
      <header className="app-page-header hidden sm:block bg-white dark:bg-gray-800 border-b-[3px] border-lime-400 mb-6">
        <div className="p-4 md:py-6 md:px-8 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <ChartBarIcon className="h-7 w-7 text-primary-600 dark:text-primary-400" />
            <div>
              <h1 className="text-[28px] font-bold text-gray-900 dark:text-white">Vereinsstatistik</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Mitgliederentwicklung und Aktivitätsanalyse · Vergleich zu {prevYear}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">{yearSelect}</div>
        </div>
      </header>

      {/* Mobile: Jahr-Auswahl */}
      <div className="sm:hidden flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-gray-900 dark:text-white">Vereinsstatistik</h1>
        {yearSelect}
      </div>

      {/* KPI-Karten mit Vorjahresvergleich */}
      {!!currentStats?.undated_exits && <p className="mb-3 rounded-xl border border-warning-200 bg-warning-50 p-3 text-sm text-warning-800 dark:border-warning-800 dark:bg-warning-900/20 dark:text-warning-300">Bei {currentStats.undated_exits} archivierten Personen mit erfasstem Eintritt fehlt das Austrittsdatum. Historische Bestände können deshalb abweichen. Das Datum kann unter Mitglieder → Archiv anzeigen → Mitgliedsdaten bearbeiten nachgetragen werden.</p>}
      <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">Bestände berücksichtigen Personen mit erfasstem Eintrittsdatum. Aktuelle Bestände und frühere Jahresendbestände werden getrennt ausgewertet.</p>
      {!!currentStats?.planned_exits_year && <p className="mb-3 text-sm text-warning-700 dark:text-warning-300">{currentStats.planned_exits_year} Kündigung(en) zum Mitgliedschaftsende in {selectedYear} vorgemerkt. Im aktuellen Bestand bleiben diese Mitglieder bis zum Austritt enthalten.</p>}
      {currentStats && (
        <div className="mb-4 sm:mb-6">
          <StatGroup columns={4} mobileColumns={4}>
            <StatCard
              icon={UserGroupIcon}
              tone="neutral"
              label={selectedYear < new Date().getFullYear() ? 'Bestand Jahresende' : 'Mitglieder aktuell'}
              value={currentStats.total_members || 0}
              sub={<YoYBadge current={currentStats.total_members || 0} previous={prevStats?.total_members} prevYear={prevYear} />}
            />
            <StatCard
              icon={CheckCircleIcon}
              tone="success"
              valueTone="success"
              label={`Aktiv ${selectedYear}`}
              value={currentStats.active_members_year || 0}
              sub={<YoYBadge current={currentStats.active_members_year || 0} previous={prevStats?.active_members_year} prevYear={prevYear} />}
            />
            <StatCard
              icon={CalendarIcon}
              tone="primary"
              valueTone="primary"
              label={`Events ${selectedYear}`}
              value={currentStats.total_events_year || 0}
              sub={<YoYBadge current={currentStats.total_events_year || 0} previous={prevStats?.total_events_year} prevYear={prevYear} />}
            />
            <StatCard
              icon={TicketIcon}
              tone="info"
              valueTone="info"
              label={`Buchungen ${selectedYear}`}
              value={currentStats.bookings_year || 0}
              sub={<YoYBadge current={currentStats.bookings_year || 0} previous={prevStats?.bookings_year} prevYear={prevYear} />}
            />
          </StatGroup>
        </div>
      )}

      {/* Tab-Navigation */}
      <div className="mb-6 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
        <div className="flex gap-1 min-w-max">
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as typeof activeTab)}
              className={`px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap border-b-2 -mb-px ${
                activeTab === tab.key
                  ? 'text-primary-600 dark:text-primary-400 border-primary-600 dark:border-primary-400'
                  : 'text-gray-600 dark:text-gray-400 border-transparent hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Ladeindikator */}
      {tabLoadingStates[activeTab] && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      )}

      {/* ==================== MITGLIEDERENTWICKLUNG ==================== */}
      {activeTab === 'growth' && !tabLoadingStates.growth && (
        <div className="space-y-6">
          <InfoBanner>
            <strong className="text-gray-900 dark:text-white">Mitgliederentwicklung:</strong> Zu- und Abgänge über die gesamte Vereinsgeschichte und im Detail für {selectedYear}.
          </InfoBanner>

          {/* Jahresvergleich */}
          {tabDataCache.memberGrowth && (() => {
            const cur = growthYearSummary(memberType, selectedYear);
            const prev = growthYearSummary(memberType, prevYear);
            return (
              <CompareStrip
                prevYear={prevYear}
                items={[
                  { label: 'Neue Mitglieder', icon: UserPlusIcon, current: cur.neu, previous: prev.neu },
                  { label: 'Ausgetreten', icon: UserMinusIcon, current: cur.aus, previous: prev.aus, invert: true },
                  { label: 'Netto-Veränderung', icon: ArrowTrendingUpIcon, current: cur.netto, previous: prev.netto },
                ]}
              />
            );
          })()}

          {/* Chart: gesamte Vereinsgeschichte */}
          {tabDataCache.memberGrowth?.[memberType]?.length ? (
            <Section title="Gesamte Vereinsgeschichte" icon={UserGroupIcon} iconAccent="info">
              <LineChart data={prepareMemberGrowthData()} type="activity" labels={{ purchases: 'Neue Mitglieder', bookings: 'Aktive Mitglieder' }} />
              <div className="mt-4 flex justify-center gap-6">
                <div className="flex items-center gap-2"><div className="w-3 h-3 bg-info-500 rounded-full"></div><span className="text-sm text-gray-600 dark:text-gray-400">Neue Mitglieder</span></div>
                <div className="flex items-center gap-2"><div className="w-3 h-3 bg-primary-500 rounded-full"></div><span className="text-sm text-gray-600 dark:text-gray-400">Aktive Mitglieder</span></div>
              </div>
            </Section>
          ) : null}

          {/* Monatliche Übersicht */}
          {tabDataCache.memberGrowth?.[memberType]?.filter(i => i.month.startsWith(selectedYear.toString())).length ? (
            <Section title={`Monatliche Übersicht ${selectedYear}`} icon={ChartBarIcon}>
              <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-2 pr-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Monat</th>
                      <th className="text-right py-2 px-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Neu</th>
                      <th className="text-right py-2 px-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Aus</th>
                      <th className="text-right py-2 px-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Netto</th>
                      <th className="text-right py-2 pl-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Gesamt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tabDataCache.memberGrowth[memberType]
                      .filter(i => i.month.startsWith(selectedYear.toString()))
                      .map((item, index) => (
                        <tr key={index} className="border-b border-gray-100 dark:border-gray-700/60">
                          <td className="py-2 pr-4 text-sm text-gray-900 dark:text-white">
                            {new Date(item.month + '-01').toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}
                          </td>
                          <td className="py-2 px-4 text-sm text-right text-success-600 dark:text-success-400">+{Number(item.new_members)}</td>
                          <td className="py-2 px-4 text-sm text-right text-error-600 dark:text-error-400">-{Number(item.deleted_members)}</td>
                          <td className={`py-2 px-4 text-sm text-right font-medium ${Number(item.net_change) >= 0 ? 'text-success-600 dark:text-success-400' : 'text-error-600 dark:text-error-400'}`}>
                            {Number(item.net_change) >= 0 ? '+' : ''}{Number(item.net_change)}
                          </td>
                          <td className="py-2 pl-4 text-sm text-right font-bold text-gray-900 dark:text-white tabular-nums">{item.active_members}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </Section>
          ) : null}
        </div>
      )}

      {/* ==================== AKTIVITÄT ==================== */}
      {activeTab === 'activity' && !tabLoadingStates.activity && (
        <div className="space-y-6">
          <InfoBanner>
            <strong className="text-gray-900 dark:text-white">Aktivitätsanalyse:</strong> Buchungsaktivität, Wochentagsverteilung und Spitzenzeiten im Jahr {selectedYear}.
          </InfoBanner>

          {currentStats && (
            <CompareStrip
              prevYear={prevYear}
              items={[
                { label: 'Buchungen', icon: TicketIcon, current: currentStats.bookings_year || 0, previous: prevStats?.bookings_year },
                { label: 'Aktive Mitglieder', icon: UserGroupIcon, current: currentStats.active_members_year || 0, previous: prevStats?.active_members_year },
                { label: 'Ø Buchungen/Mitglied', icon: ChartBarIcon, current: currentStats.avg_bookings_per_member_year || 0, previous: prevStats?.avg_bookings_per_member_year },
                { label: 'Event-Typen', icon: TagIcon, current: currentStats.unique_event_types_year || 0, previous: prevStats?.unique_event_types_year },
              ]}
            />
          )}

          {tabDataCache.memberActivity && tabDataCache.memberActivity.length > 0 && (
            <Section title="Aktivitätsverlauf" icon={SparklesIcon}>
              <LineChart data={prepareActivityData()} type="activity" labels={{ purchases: 'Aktive Mitglieder', bookings: 'Buchungen' }} />
              <div className="mt-4 flex justify-center gap-6 text-sm text-gray-600 dark:text-gray-400">
                <span>Aktive Mitglieder</span><span>Buchungen</span>
              </div>
            </Section>
          )}

          {tabDataCache.activityTrends?.weekday_distribution ? (
            <Section title="Wochentagsverteilung" icon={ClockIcon}>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-5 -mt-1">
                Alle angebotenen Veranstaltungen und deren Buchungen im Jahr {selectedYear}.
              </p>
              <div className="space-y-3">
                {(() => {
                  const weekDays = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
                  const weekdayDist = tabDataCache.activityTrends?.weekday_distribution || [];
                  const maxBookings = weekdayDist.length > 0 ? Math.max(...weekdayDist.map((d: any) => d.bookings), 1) : 1;
                  return weekDays.map((dayName) => {
                    const dayData = weekdayDist.find((d: any) => d.day === dayName);
                    const bookings = dayData?.bookings || 0;
                    const avgBookings = dayData?.avg_bookings || 0;
                    const activeDays = dayData?.active_days || 0;
                    const percentage = bookings > 0 ? (bookings / maxBookings) * 100 : 0;
                    return (
                      <div key={dayName} className="flex items-center gap-3">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 w-20 sm:w-24 flex-shrink-0">{dayName}</span>
                        <div className="flex-1 h-8 bg-gray-100 dark:bg-gray-700/60 rounded-lg border border-gray-200 dark:border-gray-600 relative overflow-hidden">
                          {activeDays > 0 && (
                            <div className="absolute left-0 top-0 h-full bg-primary-400 dark:bg-primary-600/80 transition-all duration-500" style={{ width: `${percentage}%` }} />
                          )}
                          <div className="absolute inset-0 flex items-center justify-between px-3">
                            {activeDays > 0 ? (
                              <>
                                <span className="text-xs font-semibold text-gray-800 dark:text-white z-10">{bookings} Buchungen</span>
                                <span className="text-[11px] text-gray-600 dark:text-gray-300 z-10">Ø {avgBookings}/Tag · {activeDays} Event{activeDays !== 1 ? 's' : ''}</span>
                              </>
                            ) : (
                              <span className="text-xs text-gray-400 dark:text-gray-500 italic">Keine Veranstaltungen</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </Section>
          ) : (
            <EmptyState icon={ExclamationCircleIcon} text="Keine Aktivitätsdaten verfügbar." />
          )}
        </div>
      )}

      {/* ==================== EVENTS ==================== */}
      {activeTab === 'events' && !tabLoadingStates.events && (
        <div className="space-y-6">
          <InfoBanner>
            <strong className="text-gray-900 dark:text-white">Event-Analyse:</strong> Beliebteste Veranstaltungen nach Buchungen und Auslastung im Jahr {selectedYear}.
          </InfoBanner>

          {currentStats && (
            <CompareStrip
              prevYear={prevYear}
              items={[
                { label: 'Events gesamt', icon: CalendarDaysIcon, current: currentStats.total_events_year || 0, previous: prevStats?.total_events_year },
                { label: 'Event-Typen', icon: TagIcon, current: currentStats.unique_event_types_year || 0, previous: prevStats?.unique_event_types_year },
                { label: 'Buchungen', icon: TicketIcon, current: currentStats.bookings_year || 0, previous: prevStats?.bookings_year },
              ]}
            />
          )}

          {tabDataCache.eventPopularity && tabDataCache.eventPopularity.length > 0 && (
            <Section title="Beliebte Events (Jahresübersicht)" icon={CalendarIcon}>
              <div className="space-y-3">
                {tabDataCache.eventPopularity.slice(0, 10).map((event, index) => {
                  const util = event.avg_utilization != null
                    ? event.avg_utilization
                    : event.max_teilnehmer > 0
                      ? Math.round((event.avg_bookings_per_event / event.max_teilnehmer) * 100)
                      : null;
                  return (
                    <div key={index} className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <p className="font-medium text-gray-900 dark:text-white">{event.title}</p>
                        <div className="text-right flex-shrink-0">
                          <p className="text-lg font-bold text-primary-600 dark:text-primary-400 leading-none">{util != null ? `${util}%` : 'N/A'}</p>
                          <p className="text-[11px] text-gray-500">Ø Auslastung</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                        <div className="flex items-center gap-1.5"><CalendarIcon className="h-3.5 w-3.5 text-gray-400" /><span className="text-gray-600 dark:text-gray-400">{event.event_count} Termine</span></div>
                        <div className="flex items-center gap-1.5"><UsersIcon className="h-3.5 w-3.5 text-gray-400" /><span className="text-gray-600 dark:text-gray-400">Max. {event.max_teilnehmer} Plätze</span></div>
                        <div className="flex items-center gap-1.5"><TicketIcon className="h-3.5 w-3.5 text-gray-400" /><span className="text-gray-600 dark:text-gray-400">{event.total_bookings} Buchungen</span></div>
                        <div className="flex items-center gap-1.5"><ChartBarIcon className="h-3.5 w-3.5 text-gray-400" /><span className="text-gray-600 dark:text-gray-400">Ø {event.avg_bookings_per_event}/Termin</span></div>
                      </div>
                      {(event.trainer_name || event.cotrainer_name) && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                          <AttributeLabel iconKey="trainer" textClassName="font-semibold">Leitung:</AttributeLabel> {event.trainer_name}{event.cotrainer_name && ` · Co: ${event.cotrainer_name}`}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </Section>
          )}

          {(!tabDataCache.eventPopularity || tabDataCache.eventPopularity.length === 0) && (
            <EmptyState icon={ExclamationCircleIcon} text="Keine Event-Daten verfügbar." />
          )}

          {tabDataCache.topEvents && tabDataCache.topEvents.length > 0 && (
            <Section title="Top Einzeltermine nach Buchungen" icon={ArrowTrendingUpIcon} iconAccent="success">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {tabDataCache.topEvents.slice(0, 6).map((event: any, index) => (
                  <div key={index} className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{event.title}</span>
                      <span className="text-[11px] px-2 py-0.5 bg-gray-200 dark:bg-gray-700 rounded-full flex-shrink-0">
                        {new Date(event.month + '-01').toLocaleDateString('de-DE', { month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                    {(event.trainer_name || event.cotrainer_name) && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3"><AttributeLabel iconKey="trainer" textClassName="font-semibold">Leitung:</AttributeLabel> {event.trainer_name}{event.cotrainer_name && ` · ${event.cotrainer_name}`}</p>
                    )}
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="flex flex-col">
                        <span className="text-[11px] text-gray-500">Buchungen</span>
                        <span className="font-semibold text-primary-600 dark:text-primary-400">{event.bookings} / {event.max_teilnehmer}</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[11px] text-gray-500">Auslastung</span>
                        <span className="font-semibold text-success-600 dark:text-success-400">{event.utilization_percent}%</span>
                      </div>
                      <div className="flex flex-col col-span-2">
                        <span className="text-[11px] text-gray-500">Unique Teilnehmer</span>
                        <span className="font-semibold text-gray-700 dark:text-gray-300">{event.unique_members} Personen</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>
      )}

      {/* ==================== PROFILE ==================== */}
      {activeTab === 'profiles' && !tabLoadingStates.profiles && (
        <div className="space-y-6">
          <InfoBanner>
            <strong className="text-gray-900 dark:text-white">Datenqualität:</strong> Vollständigkeit der Mitgliederprofile (aktueller Stand).
          </InfoBanner>

          {tabDataCache.profileCompleteness?.overall && (() => {
            const o = tabDataCache.profileCompleteness!.overall!;
            return (
              <CompareStrip
                prevYear={prevYear}
                items={[
                  { label: 'Ø Vollständigkeit', icon: ChartBarIcon, current: o.avg_completeness, unit: '%' },
                  { label: 'Mit Foto', icon: PhotoIcon, current: o.with_photo },
                  { label: 'Mit E-Mail', icon: EnvelopeIcon, current: o.with_email },
                  { label: 'Kritisch', icon: ExclamationTriangleIcon, current: o.missing_critical },
                ]}
              />
            );
          })()}

          {tabDataCache.profileCompleteness?.distribution && tabDataCache.profileCompleteness.distribution.length > 0 && (
            <Section title="Vollständigkeitsverteilung" icon={ChartBarIcon} iconAccent="info">
              <div className="space-y-3">
                {tabDataCache.profileCompleteness.distribution.map((item) => {
                  const barColor = item.category.includes('100%') ? 'bg-success-500'
                    : item.category.includes('80-99%') ? 'bg-info-500'
                    : item.category.includes('60-79%') ? 'bg-warning-500'
                    : item.category.includes('40-59%') ? 'bg-warning-500' : 'bg-error-500';
                  return (
                    <div key={item.category} className="flex items-center gap-3">
                      <span className="text-sm text-gray-600 dark:text-gray-400 w-32 sm:w-40 flex-shrink-0">{item.category}</span>
                      <div className="flex-1 bg-gray-100 dark:bg-gray-700/60 rounded-full h-6 relative overflow-hidden">
                        <div className={`absolute left-0 top-0 h-full ${barColor} rounded-full transition-all duration-500`} style={{ width: `${item.percentage}%` }} />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-700 dark:text-gray-200">{item.count} ({item.percentage}%)</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Section>
          )}

          {tabDataCache.profileCompleteness?.overall && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Section title="Feldabdeckung" icon={CheckCircleIcon} iconAccent="success">
                <div className="space-y-4">
                  {(() => {
                    const o = tabDataCache.profileCompleteness!.overall!;
                    const total = o.total_members || 1;
                    const fields: Array<{ name: string; Icon?: IconType; value: number; attrKey?: 'email' | 'phone' | 'birthday' | 'address' }> = [
                      { name: 'Profilfoto', Icon: PhotoIcon, value: o.with_photo },
                      { name: 'E-Mail-Adresse', value: o.with_email, attrKey: 'email' },
                      { name: 'Telefonnummer', value: o.with_phone, attrKey: 'phone' },
                      { name: 'Geburtsdatum', value: o.with_birthdate, attrKey: 'birthday' },
                      { name: 'Wohnadresse', value: o.with_address, attrKey: 'address' },
                    ];
                    return fields.map((field, index) => {
                      const percentage = Math.round(field.value / total * 100);
                      const acc: Accent = percentage >= 80 ? 'success' : percentage >= 50 ? 'warning' : 'error';
                      return (
                        <div key={index}>
                          <div className="flex items-center justify-between mb-1.5">
                            <div className="flex items-center gap-2">
                              {field.attrKey ? (
                                <AttributeLabel iconKey={field.attrKey} textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">{field.name}</AttributeLabel>
                              ) : field.Icon ? (
                                <>
                                  <field.Icon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{field.name}</span>
                                </>
                              ) : null}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500 dark:text-gray-400">{field.value}/{total}</span>
                              <span className={`text-sm font-bold ${ACCENTS[acc].value}`}>{percentage}%</span>
                            </div>
                          </div>
                          <div className="w-full h-2.5 bg-gray-100 dark:bg-gray-700/60 rounded-full overflow-hidden">
                            <div className={`h-full ${ACCENTS[acc].bar} transition-all duration-500`} style={{ width: `${percentage}%` }} />
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
                <div className="mt-5 pt-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Durchschnittliche Vollständigkeit</span>
                  <span className="font-bold text-lg text-primary-600 dark:text-primary-400">{Math.round(tabDataCache.profileCompleteness.overall.avg_completeness)}%</span>
                </div>
              </Section>

              {tabDataCache.profileCompleteness.top_incomplete && tabDataCache.profileCompleteness.top_incomplete.length > 0 && (
                <Section title="Unvollständige Profile" icon={ExclamationTriangleIcon} iconAccent="warning">
                  <div className="space-y-2">
                    {tabDataCache.profileCompleteness.top_incomplete.map((member, index) => (
                      <div key={index} className="flex items-center justify-between gap-3 p-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{member.name}</p>
                          <div className="flex gap-3 mt-0.5">
                            <span className={`text-xs ${member.has_photo ? 'text-success-600 dark:text-success-400' : 'text-error-500 dark:text-error-400'}`}>{member.has_photo ? '✓' : '✗'} Foto</span>
                            <span className={`text-xs ${member.has_email ? 'text-success-600 dark:text-success-400' : 'text-error-500 dark:text-error-400'}`}>{member.has_email ? '✓' : '✗'} E-Mail</span>
                            <span className={`text-xs ${member.has_phone ? 'text-success-600 dark:text-success-400' : 'text-error-500 dark:text-error-400'}`}>{member.has_phone ? '✓' : '✗'} Tel.</span>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-sm font-bold text-warning-600 dark:text-warning-400">{member.completeness}%</p>
                          <p className="text-[11px] text-gray-500">{member.missing_fields} fehlend</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </Section>
              )}
            </div>
          )}

          {!tabDataCache.profileCompleteness?.overall && (
            <EmptyState icon={ExclamationCircleIcon} text="Keine Profildaten verfügbar." />
          )}
        </div>
      )}

      {/* ==================== TRAINER ==================== */}
      {activeTab === 'trainers' && !tabLoadingStates.trainers && (
        <div className="space-y-6">
          <InfoBanner>
            <strong className="text-gray-900 dark:text-white">Trainer-Statistik:</strong> Erfolgreichste Trainer nach Events, Buchungen, Teilnehmern und Auslastung ({selectedYear}).
          </InfoBanner>

          {tabDataCache.topTrainers && tabDataCache.topTrainers.length > 0 && (() => {
            const cur = tabDataCache.topTrainers!;
            const prev = prevTabDataCache.topTrainers;
            const sum = (arr: TopTrainer[] | undefined, k: keyof TopTrainer) => (arr || []).reduce((s, t) => s + Number(t[k] || 0), 0);
            const avgUtil = (arr: TopTrainer[] | undefined) => (arr && arr.length ? sum(arr, 'avg_utilization') / arr.length : 0);
            return (
              <CompareStrip
                prevYear={prevYear}
                items={[
                  { label: 'Aktive Trainer', icon: AcademicCapIcon, current: cur.length, previous: prev ? prev.length : null },
                  { label: 'Events gesamt', icon: CalendarDaysIcon, current: sum(cur, 'total_events'), previous: prev ? sum(prev, 'total_events') : null },
                  { label: 'Buchungen gesamt', icon: TicketIcon, current: sum(cur, 'total_bookings'), previous: prev ? sum(prev, 'total_bookings') : null },
                  { label: 'Ø Auslastung', icon: ChartBarIcon, current: round1(avgUtil(cur)), previous: prev ? round1(avgUtil(prev)) : null, unit: '%' },
                ]}
              />
            );
          })()}

          {tabDataCache.topTrainers && tabDataCache.topTrainers.length > 0 ? (
            <Section title={`Top Trainer ${selectedYear}`} icon={TrophyIcon} iconAccent="warning">
              <div className="space-y-3">
                {tabDataCache.topTrainers.map((trainer, index) => (
                  <div key={index} className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <RankMedal rank={index + 1} />
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-900 dark:text-white truncate">{trainer.trainer_name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{trainer.event_types ? trainer.event_types.split(',').slice(0, 3).join(', ') : 'Verschiedene Events'}</p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-2xl font-bold text-primary-600 dark:text-primary-400 leading-none">{Number(trainer.total_bookings)}</p>
                        <p className="text-[11px] text-gray-500">Buchungen</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <MiniStat value={Number(trainer.total_events)} label="Events" />
                      <MiniStat value={Number(trainer.unique_participants)} label="Teilnehmer" accent="info" />
                      <MiniStat value={`${Number(trainer.avg_utilization).toFixed(1)}%`} label="Ø Auslastung" accent="success" />
                      <MiniStat value={`${Number(trainer.success_rate).toFixed(1)}%`} label="Erfolgsrate" accent="warning" />
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400 mt-3">
                      <span className="inline-flex items-center gap-1"><CalendarIcon className="h-3.5 w-3.5" />{Number(trainer.unique_event_types)} Event-Typen</span>
                      <span className="inline-flex items-center gap-1"><UsersIcon className="h-3.5 w-3.5" />Ø {trainer.avg_bookings_per_event} Buchungen/Event</span>
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          ) : (
            <EmptyState icon={MegaphoneIcon} text={`Keine Trainer-Daten für ${selectedYear} verfügbar.`} />
          )}
        </div>
      )}

      {/* ==================== TOP MITGLIEDER ==================== */}
      {activeTab === 'members' && !tabLoadingStates.members && (
        <div className="space-y-6">
          <InfoBanner>
            <strong className="text-gray-900 dark:text-white">Aktivste Mitglieder:</strong> Top 10 nach Buchungen im Jahr {selectedYear} – Teilnahmehäufigkeit und Eventvielfalt.
          </InfoBanner>

          {tabDataCache.topMembers && tabDataCache.topMembers.length > 0 && (() => {
            const cur = tabDataCache.topMembers!;
            const prev = prevTabDataCache.topMembers;
            const sum = (arr: TopMember[] | undefined, k: keyof TopMember) => (arr || []).reduce((s, m) => s + Number(m[k] || 0), 0);
            const avg = (arr: TopMember[] | undefined, k: keyof TopMember) => (arr && arr.length ? sum(arr, k) / arr.length : 0);
            return (
              <CompareStrip
                prevYear={prevYear}
                items={[
                  { label: 'Buchungen (Top 10)', icon: TicketIcon, current: sum(cur, 'total_bookings'), previous: prev ? sum(prev, 'total_bookings') : null },
                  { label: 'Ø Buchungen/Mitglied', icon: ChartBarIcon, current: round1(avg(cur, 'total_bookings')), previous: prev ? round1(avg(prev, 'total_bookings')) : null },
                  { label: 'Ø Aktive Tage', icon: CalendarDaysIcon, current: round1(avg(cur, 'active_days')), previous: prev ? round1(avg(prev, 'active_days')) : null },
                ]}
              />
            );
          })()}

          {tabDataCache.topMembers && tabDataCache.topMembers.length > 0 ? (
            <Section title={`Top 10 Mitglieder ${selectedYear}`} icon={UserGroupIcon} iconAccent="info">
              <div className="space-y-3">
                {tabDataCache.topMembers.map((member, index) => (
                  <div key={index} className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <RankMedal rank={index + 1} />
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-900 dark:text-white truncate">{member.member_name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Aktiv seit {new Date(member.first_booking).toLocaleDateString('de-DE')}</p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-2xl font-bold text-info-600 dark:text-info-400 leading-none">{member.total_bookings}</p>
                        <p className="text-[11px] text-gray-500">Buchungen</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                      <MiniStat value={member.unique_event_types} label="Event-Typen" />
                      <MiniStat value={member.active_days} label="Aktive Tage" accent="info" />
                      <MiniStat value={`${member.participation_rate}%`} label="Teilnahmerate" accent="success" />
                      <MiniStat value={member.avg_bookings_per_month} label="Ø/Monat" accent="primary" />
                    </div>
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
                      <p className="text-xs text-gray-600 dark:text-gray-400"><strong>Bevorzugte Events:</strong> {member.event_types ? member.event_types.split(',').slice(0, 5).join(', ') : 'Verschiedene Events'}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5"><AttributeLabel iconKey="date" size="sm">Letzte Buchung:</AttributeLabel> {new Date(member.last_booking).toLocaleDateString('de-DE')}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          ) : (
            <EmptyState icon={UserGroupIcon} text={`Keine Mitgliederdaten für ${selectedYear} verfügbar.`} />
          )}
        </div>
      )}

      {/* ==================== GÄSTEKARTEN ==================== */}
      {activeTab === 'guestcards' && !tabLoadingStates.guestcards && tabDataCache.guestCardStats && (
        <div className="space-y-6">
          <InfoBanner>
            <strong className="text-gray-900 dark:text-white">Gästekarten:</strong> Verkäufe, Einnahmen und Nutzung im Jahr {selectedYear}.
          </InfoBanner>

          {(() => {
            const g = tabDataCache.guestCardStats!;
            const pg = prevTabDataCache.guestCardStats;
            return (
              <CompareStrip
                prevYear={prevYear}
                items={[
                  { label: 'Gesamtumsatz', icon: CurrencyEuroIcon, current: g.revenue.total || 0, previous: pg ? pg.revenue.total || 0 : null, money: true },
                  { label: 'Verkaufte Karten', icon: TicketIcon, current: g.cards.total || 0, previous: pg ? pg.cards.total || 0 : null },
                  { label: 'Buchungen', icon: CalendarIcon, current: g.bookings.total || 0, previous: pg ? pg.bookings.total || 0 : null },
                  { label: 'Offen', icon: ExclamationCircleIcon, current: g.revenue.unpaid || 0, previous: pg ? pg.revenue.unpaid || 0 : null, money: true, invert: true },
                ]}
              />
            );
          })()}

          {tabDataCache.guestCardStats.typeDistribution && tabDataCache.guestCardStats.typeDistribution.length > 0 && (
            <Section title="Kartentyp-Verteilung" icon={ChartBarIcon} iconAccent="amber">
              <div className="space-y-3">
                {tabDataCache.guestCardStats.typeDistribution.map((type, index) => (
                  <div key={index} className="p-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900 dark:text-white">{type.name}</span>
                      <span className="text-sm font-bold text-success-600 dark:text-success-400">{fmtEUR(type.revenue)}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div><span className="text-gray-500">Verkauft:</span> <span className="font-medium text-gray-700 dark:text-gray-300">{type.count}</span></div>
                      <div><span className="text-gray-500">Ø Rest:</span> <span className="font-medium text-gray-700 dark:text-gray-300">{type.avgRemaining}%</span></div>
                      <div><span className="text-gray-500">Ø Preis:</span> <span className="font-medium text-gray-700 dark:text-gray-300">{fmtEUR(type.count ? type.revenue / type.count : 0)}</span></div>
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {tabDataCache.guestCardStats.monthlyRevenue && tabDataCache.guestCardStats.monthlyRevenue.length > 0 && (
            <Section title="Monatliche Einnahmen" icon={ArrowTrendingUpIcon} iconAccent="amber">
              <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-2 pr-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Monat</th>
                      <th className="text-right py-2 px-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Umsatz</th>
                      <th className="text-right py-2 px-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Verkauft</th>
                      <th className="text-right py-2 pl-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Käufer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tabDataCache.guestCardStats.monthlyRevenue.map((month, index) => (
                      <tr key={index} className="border-b border-gray-100 dark:border-gray-700/60">
                        <td className="py-2 pr-4 text-sm text-gray-900 dark:text-white">{new Date(month.month + '-01').toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}</td>
                        <td className="py-2 px-4 text-sm text-right font-semibold text-success-600 dark:text-success-400">{fmtEUR(month.revenue)}</td>
                        <td className="py-2 px-4 text-sm text-right text-gray-900 dark:text-white tabular-nums">{month.cardsSold}</td>
                        <td className="py-2 pl-4 text-sm text-right text-gray-900 dark:text-white tabular-nums">{month.uniqueBuyers}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-white dark:bg-[#1c2432] rounded-2xl p-4 border border-gray-200 dark:border-gray-700">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Durchschnittswerte</h4>
              <div className="space-y-2">
                <div className="flex justify-between"><span className="text-xs text-gray-500 dark:text-gray-400">Einträge pro Karte:</span><span className="text-xs font-medium text-gray-900 dark:text-white">{Number(tabDataCache.guestCardStats.averages.entriesPerCard || 0).toFixed(1)}</span></div>
                <div className="flex justify-between"><span className="text-xs text-gray-500 dark:text-gray-400">Preis pro Karte:</span><span className="text-xs font-medium text-gray-900 dark:text-white">{fmtEUR(tabDataCache.guestCardStats.averages.pricePerCard || 0)}</span></div>
              </div>
            </div>
            <div className="bg-white dark:bg-[#1c2432] rounded-2xl p-4 border border-gray-200 dark:border-gray-700">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Kartennutzung</h4>
              <div className="space-y-2">
                <div className="flex justify-between"><span className="text-xs text-gray-500 dark:text-gray-400">Aufgebraucht:</span><span className="text-xs font-medium text-gray-900 dark:text-white">{tabDataCache.guestCardStats.cards.depleted}</span></div>
                <div className="flex justify-between"><span className="text-xs text-gray-500 dark:text-gray-400">Events gebucht:</span><span className="text-xs font-medium text-gray-900 dark:text-white">{tabDataCache.guestCardStats.bookings.uniqueEvents}</span></div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberStatistics;
